import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout/Layout';
import axios from 'axios';
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API_BASE = "https://api.mbsmartweb.com/api";

const MyDirectTeam = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [teamData, setTeamData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Stats from API
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0
  });

  // Fetch direct members from API with pagination
  const fetchDirectMembers = useCallback(async () => {
    const UserId = localStorage.getItem("UserId");

    setLoading(true);
    setError("");
    try {
      const response = await axios.post(`${API_BASE}/directmember-paginated`, {
        UserId,
        page: currentPage,
        limit: entriesPerPage,
        search: searchTerm,
        status: filterStatus
      });

      if (response.data.success) {
        setTeamData(response.data.data);
        setTotalRecords(response.data.total);
        // Update stats from API response
        if (response.data.stats) {
          setStats(response.data.stats);
        }
      } else {
        setTeamData([]);
        setError(response.data.error || "No data found");
      }
    } catch (err) {
      console.error(err);
      setTeamData([]);
      setError(err.response?.data?.error || "Failed to fetch data");
      toast.error("Failed to load team data");
    } finally {
      setLoading(false);
    }
  }, [currentPage, entriesPerPage, searchTerm, filterStatus]);

  useEffect(() => {
    fetchDirectMembers();
  }, [fetchDirectMembers]);

  // Fetch stats separately
  const fetchStats = async () => {
    const UserId = localStorage.getItem("UserId");
    try {
      const response = await axios.post(`${API_BASE}/directmember-stats`, { UserId });
      if (response.data.success) {
        setStats(response.data.stats);
        console.log(response.data.stats);
      }
    } catch (err) {
      console.error("Error fetching stats:", err);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const StatusBadge = ({ status }) => {
    const isActive = status === 1 || status === "1" || status === "Active";
    let label = "Inactive";
    let color = "#ef4444";
    
    if (isActive) {
      label = "Active";
      color = "#10b981";
    } else if (status === "Pending") {
      label = "Pending";
      color = "#f59e0b";
    }

    return (
      <span className={`status-badge ${label.toLowerCase()}`} style={{ backgroundColor: color }}>
        {label}
      </span>
    );
  };

  const handleEntriesChange = (e) => {
    setEntriesPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const exportToCSV = async () => {
    const UserId = localStorage.getItem("UserId");
    toast.info("Preparing export...");
    
    try {
      const response = await axios.post(`${API_BASE}/directmember-export`, {
        UserId,
        search: searchTerm,
        status: filterStatus
      }, { responseType: 'blob' });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `direct_team_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Report exported successfully!");
    } catch (err) {
      console.error("Export error:", err);
      toast.error("Failed to export report");
    }
  };

  const getPaginationButtons = () => {
    const total = Math.ceil(totalRecords / entriesPerPage);
    const buttons = [];
    const maxVisible = 5;
    let start = Math.max(1, currentPage - 2);
    let end = Math.min(total, start + maxVisible - 1);
    
    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }
    
    if (start > 1) {
      buttons.push(
        <button key="first" onClick={() => setCurrentPage(1)} className="page-btn">
          1
        </button>
      );
      if (start > 2) buttons.push(<span key="dots1" className="page-dots">...</span>);
    }
    
    for (let i = start; i <= end; i++) {
      buttons.push(
        <button
          key={i}
          onClick={() => setCurrentPage(i)}
          className={`page-btn ${currentPage === i ? 'active-page' : ''}`}
        >
          {i}
        </button>
      );
    }
    
    if (end < total) {
      if (end < total - 1) buttons.push(<span key="dots2" className="page-dots">...</span>);
      buttons.push(
        <button key="last" onClick={() => setCurrentPage(total)} className="page-btn">
          {total}
        </button>
      );
    }
    
    return buttons;
  };

  return (
    <Layout>
      <ToastContainer position="top-center" autoClose={3000} />
      <div className="team-page">
        <div className="page-header">
          <div>
            <h1>My Direct Team</h1>
            <p>Manage and track your direct referrals</p>
          </div>
          {/* <button className="btn-primary" onClick={exportToCSV}>
            <i className="fa fa-download"></i> Export Report
          </button> */}
        </div>

        {/* Stats Cards - Only Total, Active, Inactive */}
        <div className="stats-wrapper">
          <div className="stats-grid">
            <div className="stat-card gradient-primary">
              <div className="stat-icon"><i className="fa fa-users"></i></div>
              <div className="stat-info">
                <h3>{stats.total.toLocaleString()}</h3>
                <p>Total Members</p>
              </div>
            </div>
            <div className="stat-card gradient-success">
              <div className="stat-icon"><i className="fa fa-user-check"></i></div>
              <div className="stat-info">
                <h3>{stats.active.toLocaleString()}</h3>
                <p>Active</p>
              </div>
            </div>
            <div className="stat-card gradient-danger">
              <div className="stat-icon"><i className="fa fa-user-times"></i></div>
              <div className="stat-info">
                <h3>{stats.inactive.toLocaleString()}</h3>
                <p>Inactive</p>
              </div>
            </div>
          </div>
        </div>

        {/* DataTable Controls */}
        <div className="datatable-controls">
          <div className="datatable-length">
            <label>Show</label>
            <select value={entriesPerPage} onChange={handleEntriesChange}>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <label>entries</label>
          </div>
          <div className="datatable-search">
            <label>Search:</label>
            <input
              type="text"
              placeholder="Search by User ID or Name..."
              value={searchTerm}
              onChange={handleSearch}
            />
          </div>
        </div>

        {/* DataTable - Desktop View */}
        <div className="datatable-container desktop-view">
          <table className="datatable">
            <thead>
              <tr>
                <th>#</th>
                <th>User ID</th>
                <th>Name</th>
                <th>Mobile</th>
                <th>Rank</th>
                <th>Join Date</th>
                <th>Status</th>
                {/* <th>Action</th> */}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr className="loading-row">
                  <td colSpan="7">
                    <div className="loader"></div> Loading...
                   </td>
                </tr>
              ) : error ? (
                <tr className="error-row">
                  <td colSpan="7">
                    <i className="fa fa-exclamation-circle"></i> {error}
                   </td>
                </tr>
              ) : teamData.length === 0 ? (
                <tr className="empty-row">
                  <td colSpan="7">
                    <i className="fa fa-users"></i> No team members found
                   </td>
                </tr>
              ) : (
                teamData.map((member, index) => (
                  <tr key={member.Userid || index}>
                    <td>{((currentPage - 1) * entriesPerPage) + index + 1}</td>
                    <td><span className="user-id">{member.Userid}</span></td>
                    <td>{member.Name}</td>
                    <td>{member.Mobile}</td>
                    <td>{member.RankName}</td>
                    <td>{new Date(member.Dateofjoining).toLocaleDateString()}</td>
                    <td><StatusBadge status={member.Status} /></td>
                    {/* <td>
                      <button className="view-btn" title="View Details">
                        <i className="fa fa-eye"></i>
                      </button>
                    </td> */}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* DataTable - Mobile Card View */}
        <div className="datatable-container mobile-view">
          {loading ? (
            <div className="mobile-loading">
              <div className="loader"></div> Loading...
            </div>
          ) : error ? (
            <div className="mobile-error">
              <i className="fa fa-exclamation-circle"></i> {error}
            </div>
          ) : teamData.length === 0 ? (
            <div className="mobile-empty">
              <i className="fa fa-users"></i> No team members found
            </div>
          ) : (
            teamData.map((member, index) => (
              <div key={member.Userid || index} className="mobile-card">
                <div className="mobile-card-header">
                  <span className="mobile-serial">#{((currentPage - 1) * entriesPerPage) + index + 1}</span>
                  <StatusBadge status={member.Status} />
                </div>
                <div className="mobile-card-body">
                  <div className="mobile-row">
                    <span className="mobile-label">User ID:</span>
                    <span className="mobile-value user-id">{member.Userid}</span>
                  </div>
                  <div className="mobile-row">
                    <span className="mobile-label">Name:</span>
                    <span className="mobile-value">{member.Name}</span>
                  </div>
                  <div className="mobile-row">
                    <span className="mobile-label">Mobile:</span>
                    <span className="mobile-value">{member.Mobile}</span>
                  </div>
                  <div className="mobile-row">
                    <span className="mobile-label">Join Date:</span>
                    <span className="mobile-value">{new Date(member.Dateofjoining).toLocaleDateString()}</span>
                  </div>
                </div>
                <div className="mobile-card-footer">
                  <button className="view-btn" title="View Details">
                    <i className="fa fa-eye"></i> View Details
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Pagination */}
        {totalRecords > entriesPerPage && !loading && !error && (
          <div className="datatable-footer">
            <div className="datatable-info">
              Showing {((currentPage - 1) * entriesPerPage) + 1} to {Math.min(currentPage * entriesPerPage, totalRecords)} of {totalRecords.toLocaleString()} entries
            </div>
            <div className="datatable-pagination">
              <button 
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} 
                disabled={currentPage === 1}
                className="page-nav"
              >
                <i className="fa fa-chevron-left"></i>
              </button>
              {getPaginationButtons()}
              <button 
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(totalRecords / entriesPerPage)))} 
                disabled={currentPage === Math.ceil(totalRecords / entriesPerPage)}
                className="page-nav"
              >
                <i className="fa fa-chevron-right"></i>
              </button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default MyDirectTeam;